import React from 'react';
import { StatusBadge } from '../common';
import { formatPriceDisplay } from '../../utils/currencyUtils';
import { formatSessionTimeDisplay } from '../../utils/homeSessionUtils';
import { SessionTypeIcon, sessionTypeTone } from '../clubs/dashboard/SessionTypeChip';
import { SessionClubLogoLink } from './SessionClubLogoLink';
import { isHostSelectSignup } from '../../utils/signupSelection';

function getSessionTimes(session) {
  const startRaw = session.start_time || session.time;
  const start = formatSessionTimeDisplay(startRaw);
  const end = session.end_time ? formatSessionTimeDisplay(session.end_time) : null;
  const hasEnd = Boolean(end && end !== 'TBD' && end !== start);

  let compact = start;
  if (hasEnd) {
    const startPeriod = start.slice(-2);
    const endPeriod = end.slice(-2);
    compact = startPeriod === endPeriod
      ? `${start.slice(0, -3)}–${end}`
      : `${start} – ${end}`;
  }

  return { start, end: hasEnd ? end : null, compact };
}

function toCount(value) {
  if (Array.isArray(value)) return value.length;
  return Number(value) || 0;
}

function getCapacity(session) {
  const confirmed = toCount(session.confirmed_players ?? session.current_players);
  const max = session.max_players || 16;
  const waitlist = toCount(session.waitlist_players);
  const fillPercent = max > 0 ? Math.min(100, Math.round((confirmed / max) * 100)) : 0;
  return { confirmed, max, waitlist, fillPercent };
}

function getSignupStatusLabel(session, userSignupStatus) {
  if (!userSignupStatus) return null;
  switch (userSignupStatus.signup_status) {
    case 'confirmed':
      return isHostSelectSignup(session) ? 'Confirmed' : 'Signed up';
    case 'waitlist':
      return `Waitlist #${userSignupStatus.waitlist_position}`;
    default:
      return null;
  }
}

function ChevronIcon() {
  return (
    <svg
      className="h-5 w-5 shrink-0 text-text-muted"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

/**
 * Compact timeline row for session discovery (Find Sessions).
 * The whole card opens details — signup happens in the modal.
 */
const SessionEventRow = ({
  session,
  userSignupStatus,
  onViewDetails,
  memberClubIds,
  compact = false,
  className = '',
}) => {
  const isCancelled = session.session_status === 'cancelled';
  const isSignedUp =
    userSignupStatus?.signup_status === 'confirmed' ||
    userSignupStatus?.signup_status === 'waitlist';

  const isSignupDeadlinePassed = (() => {
    if (!session.signup_deadline_hours) return false;
    const datePart = (session.session_date || '').split('T')[0];
    if (!datePart) return false;
    const deadline = new Date(datePart);
    if (Number.isNaN(deadline.getTime())) return false;
    deadline.setHours(deadline.getHours() - session.signup_deadline_hours);
    return new Date() > deadline;
  })();

  const capacity = getCapacity(session);
  const signupStatusLabel = getSignupStatusLabel(session, userSignupStatus);
  const clubName = session.clubs?.name || session.clubName;
  const clubId = session.club_id || session.clubs?.id;
  const location = session.location || session.clubs?.location;
  const isClubMember = Boolean(memberClubIds && clubId && memberClubIds.has(clubId));
  const times = getSessionTimes(session);
  const courtCount = session.number_of_courts || 1;
  const courtsLabel = `${courtCount} court${courtCount !== 1 ? 's' : ''}`;
  const priceLabel = formatPriceDisplay(session.price_per_player, session.currency_code);
  const sessionTitle = session.name || 'Badminton session';
  const sessionTypeName = session.session_types?.name;
  const typeTone = sessionTypeTone(session);
  const metaParts = [
    capacity.waitlist > 0 ? `${capacity.waitlist} waitlist` : null,
    courtsLabel,
    priceLabel,
    isSignupDeadlinePassed && !isSignedUp && !isCancelled ? 'Signup closed' : null,
  ].filter(Boolean);

  const openDetails = () => {
    if (onViewDetails) onViewDetails(session.id);
  };

  return (
    <li
      className={`
        relative overflow-hidden rounded-2xl border bg-surface shadow-brand-sm transition-shadow
        ${isCancelled
          ? 'border-danger/30 bg-danger-light/10 opacity-80'
          : 'border-border-medium hover:border-border-strong hover:shadow-brand-md'}
        ${className}
      `}
    >
      <button
        type="button"
        onClick={openDetails}
        aria-label={`View details for ${sessionTitle}`}
        className="absolute inset-0 z-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
      />
      <div className="pointer-events-none relative z-10 flex items-center gap-3 p-4 md:gap-5">
        <time
          dateTime={session.start_time || undefined}
          className={`w-[5.75rem] shrink-0 flex-col items-center justify-center rounded-lg border border-border-light bg-surface-subtle px-2 py-3 text-center ${compact ? 'hidden' : 'hidden md:flex'}`}
        >
          <span className="whitespace-nowrap text-sm font-bold tabular-nums leading-none text-text-primary">
            {times.start}
          </span>
          {times.end ? (
            <span className="mt-1.5 whitespace-nowrap text-xs font-medium tabular-nums text-text-muted">
              {times.end}
            </span>
          ) : null}
        </time>

        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              {clubName ? (
                <span className="pointer-events-auto">
                  <SessionClubLogoLink
                    club={session.clubs}
                    clubId={clubId}
                    clubName={clubName}
                    isClubMember={isClubMember}
                    size="sm"
                  />
                </span>
              ) : null}
              <h3 className="min-w-0 truncate text-base font-semibold text-text-primary">
                {sessionTitle}
              </h3>
              {sessionTypeName ? (
                <span
                  className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${typeTone.chip}`}
                  title={sessionTypeName}
                  aria-label={sessionTypeName}
                >
                  <SessionTypeIcon session={session} className="h-5 w-5" />
                </span>
              ) : null}
              {isCancelled ? (
                <StatusBadge variant="danger" size="small" className="shrink-0">
                  Cancelled
                </StatusBadge>
              ) : null}
            </div>
            <time
              dateTime={session.start_time || undefined}
              className={`shrink-0 whitespace-nowrap text-sm font-semibold tabular-nums text-text-primary ${compact ? '' : 'md:hidden'}`}
            >
              {times.compact}
            </time>
          </div>

          {(clubName || location || signupStatusLabel) ? (
            <div className={`flex items-center gap-3 ${clubName || location ? 'justify-between' : 'justify-end'}`}>
              {(clubName || location) ? (
                <p className="min-w-0 truncate text-sm text-text-secondary">
                  {clubName}
                  {clubName && location ? (
                    <span className="text-text-muted"> · {location}</span>
                  ) : location}
                </p>
              ) : null}
              {signupStatusLabel ? (
                <span
                  className={`shrink-0 text-xs font-medium ${
                    userSignupStatus?.signup_status === 'confirmed'
                      ? 'text-success-dark'
                      : 'text-warning-dark'
                  }`}
                >
                  {signupStatusLabel}
                </span>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-text-secondary">
            <div className="flex min-w-[8.5rem] max-w-[13rem] flex-1 items-center gap-2">
              <span className="shrink-0 text-xs font-semibold tabular-nums text-text-primary">
                {capacity.confirmed}/{capacity.max}
              </span>
              <div
                className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-border-light"
                role="progressbar"
                aria-label="Confirmed spots filled"
                aria-valuemin={0}
                aria-valuemax={capacity.max}
                aria-valuenow={capacity.confirmed}
              >
                <div
                  className={`h-full rounded-full ${
                    capacity.confirmed >= capacity.max ? 'bg-warning' : 'bg-primary'
                  }`}
                  style={{ width: `${capacity.fillPercent}%` }}
                />
              </div>
            </div>
            {metaParts.map((part) => (
              <React.Fragment key={part}>
                <span className="text-border-medium" aria-hidden>·</span>
                <span className={part === 'Signup closed' ? 'text-warning-dark' : undefined}>
                  {part}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>

        <ChevronIcon />
      </div>
    </li>
  );
};

export default SessionEventRow;
